import { Box, Button, FormField, Heading, TextButton } from '@wix/design-system'
import { ChevronLeft } from '@wix/wix-ui-icons-common'
import cn from 'classnames'
import React, { FC, useMemo, useRef } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { useGridAnimationBackground } from '../../../hooks/useGridAnimationBackground'
import { ContentWithLoading } from '../../reusable/Button'
import { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import { AutoCompleteWithOverrides } from '../../reusable/Category/AutoCompleteWithOverrides'
import { SuggestionItem } from '../../reusable/Category/SuggestionItem'
import { useCategory } from '../../reusable/Category/useCategory'
import { FancyAutoComplete } from '../../reusable/FancyAutoComplete'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import s from './index.scss'

export const CardCategory: FC = () => {
  const businessTermService = useMemo(() => new BusinessTermService(ambassadorHttpClient), [])
  const backgroundRef = useRef<HTMLDivElement>(null)
  useGridAnimationBackground(backgroundRef)

  const {
    handlers: {
      handleClear,
      handleInputClick,
      handleSearchSelect,
      handleNextClick,
      handleSearchChange,
      handleSkipClick,
      handleBackClick,
      handleSuggestionClick,
    },
    state: { search = '', category, categoriesLoading, clickedButton },
    categoriesOptions,
    content: {
      isAddUserInputAsOption,
      placeholder,
      title,
      subtitle,
      suggestionsLabel,
      suggestions,
      nextButton,
      skipButton,
      backButton,
    },
    autoCompleteRef,
    selectedId,
  } = useCategory({
    businessTermService,
  })

  return (
    <div className={s.CardCategory__layout}>
      <div ref={backgroundRef} className={s.CardCategory__animatedBackground} />
      <div className={s.CardCategory__logo}>
        <WixLogo />
      </div>
      <div className={s.CardCategory__card}>
        <div className={s.CardCategory__cardContent}>
          <div className={cn(s.CardCategory__heading, { [s.CardCategory__headingWithSubtitle]: subtitle })}>
            <Heading className={s.CardCategory__title}>{title}</Heading>
            {Boolean(subtitle) && (
              <Heading className={s.CardCategory__subtitle} size="tiny">
                {subtitle}
              </Heading>
            )}
          </div>
          <Box gap="24px" verticalAlign="middle">
            <FormField classNames={s.CardCategory__formField}>
              <FancyAutoComplete
                popoverProps={{ moveBy: { y: 0 } }}
                ref={autoCompleteRef}
                value={search}
                selectedId={selectedId}
                onChange={handleSearchChange}
                status={categoriesLoading ? 'loading' : undefined}
                options={categoriesOptions}
                placeholder={placeholder}
                size="large"
                onSelect={handleSearchSelect}
                onInputClicked={handleInputClick}
                maxHeightPixels="300"
                className={s.CardCategory__autoComplete}
                border="bottomLine"
                onClear={categoriesLoading ? undefined : handleClear}
                customRenderer={AutoCompleteWithOverrides}
                isIconHidden={isAddUserInputAsOption}
              />
            </FormField>
            {/* came from funnel-intro-next */}
            {/* as per Figma, for this specific page padding should be smaller on the right */}
            {/* so that the button "looks better" (c) @yanitg */}
            <Button disabled={!category} onClick={handleNextClick} style={{ marginRight: -40 }} size="medium">
              <ContentWithLoading isLoading={clickedButton === 'primary'}>{nextButton}</ContentWithLoading>
            </Button>
          </Box>
          <Box direction="vertical" gap="9px" align="left">
            <Box marginTop="SP2">
              <Heading className={s.CardCategory__examplesTitle}>{suggestionsLabel}</Heading>
            </Box>
            {suggestions.items.map((suggestion, index) => (
              <SuggestionItem key={index} text={suggestion.text} index={index} onClick={handleSuggestionClick} />
            ))}
          </Box>
        </div>
      </div>
      <div className={s.CardCategory__footer}>
        <TextButton
          skin="dark"
          size="medium"
          onClick={handleBackClick}
          prefixIcon={<ChevronLeft />}
          dataHook="back-button"
        >
          {backButton}
        </TextButton>
        <TextButton skin="dark" size="medium" onClick={handleSkipClick} dataHook="skip-button">
          {skipButton}
        </TextButton>
      </div>
    </div>
  )
}
